// src/pages/SubmissionSuccess.jsx
import { Link, useLocation } from "react-router-dom";
import { COMPANY } from "../constants/company";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n || 0);

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(d);
  }
};

const Field = ({ label, value, mono = false }) => (
  <div className="py-3 border-b border-slate-100 last:border-b-0 grid grid-cols-12 gap-3">
    <dt className="col-span-5 text-xs uppercase tracking-wider font-semibold text-slate-500">
      {label}
    </dt>
    <dd
      className={`col-span-7 text-sm font-medium text-slate-900 break-words ${
        mono ? "font-mono" : ""
      }`}
    >
      {value || "—"}
    </dd>
  </div>
);

const DocRow = ({ label, doc }) => (
  <div className="flex items-center justify-between py-4 border-b border-slate-100 last:border-b-0">
    <div className="flex items-center gap-3">
      <div
        className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          doc?.url ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"
        }`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {doc?.url ? "Verified and stored" : "Not uploaded"}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-3">
      {doc?.url && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider">
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Verified
        </span>
      )}
      {doc?.url ? (
        <a
          href={doc.url}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 underline-offset-4 hover:underline"
        >
          View
        </a>
      ) : (
        <span className="text-xs text-slate-400">—</span>
      )}
    </div>
  </div>
);

export default function SubmissionSuccess() {
  const { state } = useLocation();
  const data = state?.data || state || {};

  const {
    fullName,
    email,
    mobile,
    pan,
    dob,
    amount,
    paymentDate,
    txnId,
    agentName,
    panDoc,
    aadharDoc,
    createdAt,
  } = data || {};

  const referenceNo = txnId ? `REF-${txnId}` : "—";
  const invoiceUrl = `${COMPANY.API_BASE_URL}/api/invoice/${txnId}`;

  return (
    <div className="min-h-screen bg-slate-50 py-8 sm:py-12 px-4">
      <div className="mx-auto max-w-3xl">
        {/* Brand strip */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-slate-900 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 text-white">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
              </svg>
            </div>
            <span className="text-sm font-bold text-slate-900 tracking-tight">
              {COMPANY.NAME}
            </span>
          </div>
          <span className="text-xs text-slate-500">{formatDate(createdAt)}</span>
        </div>

        {/* ===== Card ===== */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Status bar */}
          <div className="h-1 bg-emerald-500" />

          {/* Header */}
          <div className="px-6 sm:px-10 pt-8 pb-6 border-b border-slate-100">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                    Payment Received
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                  Submission Confirmed
                </h1>
                <p className="mt-2 text-sm text-slate-600">
                  Thank you, {fullName || "Customer"}. Your KYC details and
                  payment have been recorded.
                </p>
              </div>
              <div className="hidden sm:block text-right shrink-0">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  Reference
                </p>
                <p className="mt-1 font-mono text-sm font-bold text-slate-900">
                  {referenceNo}
                </p>
              </div>
            </div>
          </div>

          {/* Amount summary */}
          <div className="px-6 sm:px-10 py-6 bg-slate-50 border-b border-slate-100">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  Amount Paid
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-900 tracking-tight">
                  {inr(amount)}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  Payment Date
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {paymentDate || "—"}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  Transaction ID
                </p>
                <p className="mt-1 text-sm font-mono font-semibold text-slate-900 truncate">
                  {txnId || "—"}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  Agent
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900 truncate">
                  {agentName || "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Customer details */}
          <div className="px-6 sm:px-10 py-6">
            <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
              Customer Details
            </h2>
            <dl>
              <Field label="Full Name" value={fullName} />
              <Field label="Email" value={email} />
              <Field label="Mobile" value={mobile} />
              <Field label="PAN Number" value={pan} mono />
              <Field label="Date of Birth" value={dob} />
            </dl>
          </div>

          {/* Documents */}
          <div className="px-6 sm:px-10 py-6 border-t border-slate-100">
            <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
              KYC Documents
            </h2>
            <div>
              <DocRow label="PAN Document" doc={panDoc} />
              <DocRow label="Aadhar Document" doc={aadharDoc} />
            </div>
          </div>

          {/* Notice */}
          <div className="px-6 sm:px-10 py-5 bg-slate-50 border-t border-slate-100">
            <div className="flex items-start gap-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 text-slate-500 shrink-0 mt-0.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-xs text-slate-600 leading-relaxed">
                A confirmation email with the tax invoice has been sent to{" "}
                <span className="font-semibold text-slate-900">{email}</span>.
                Please check your spam folder if it does not arrive within a few minutes.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 sm:px-10 py-6 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <a
              href={invoiceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Invoice
            </a>
            <Link
              to="/agreement"
              state={{ fullName, email, mobile }}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
            >
              Continue to Agreement
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 px-1">
          <Link
            to="/"
            className="text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors"
          >
            ← Submit another response
          </Link>
          <p className="text-xs text-slate-400">
            Need help? Contact{" "}
            <a
              href={`mailto:${COMPANY.CONTACT?.EMAIL || "support@alstockmantra.in"}`}
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              {COMPANY.CONTACT?.EMAIL || "support@alstockmantra.in"}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
